import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Store from 'store';
import { getStats } from 'logic/stats';
import { getSkillInfo } from 'logic/skills';
import Locale from 'data/locale';
import Tooltip from 'ui/tooltip';
import Slider from 'ui/slider';
import { TooltipInput } from 'tooltips';

const _L = Locale("ui-skills.js", "skilldata");

export const SkillIcon = React.forwardRef(({ charClass, info, alt }, ref) => (
  <span className={classNames(`d3p-skill-icon d3p-class-${charClass}`, charClass === "attack" && `d3p-${info.id}`, {"d3p-empty": !info})}
        style={(!info || charClass === "attack") ? null : ({backgroundPosition: `${-info.col}em ${-2 * info.row}em`})} ref={ref}>
    <span className="d3p-skill-frame"/>
    {!info && alt != null && <span className="d3p-skill-text">{alt}</span>}
  </span>
));

export const PassiveIcon = React.forwardRef(({ charClass, info, alt }, ref) => (
  <span className={classNames(`d3p-passive-icon d3p-class-${charClass}`, {"d3p-empty": !info})}
        style={info ? ({backgroundPosition: `${-info.index}em 0`}) : null} ref={ref}>
    <span className="d3p-passive-frame"/>
    {!info && alt != null && <span className="d3p-passive-text">{alt}</span>}
  </span>
));

export const ItemIcon = React.forwardRef(function ItemIcon({ id }, ref) {
  const charClass = Store.useProfile(profile => profile.class);
  const gender = Store.useProfile(profile => profile.gender);
  const item = Data.itemById[id];
  const slot = item && Data.itemTypes[item.type]?.slot;
  return <span className={classNames("d3p-item-icon", slot && `d3p-slot-${slot}`)} style={{backgroundImage: `url(${Data.getItemIcon(id, {class: charClass, gender})})`}} ref={ref}/>;
});

export const SkillHeader = React.forwardRef(({ icon, empty, name, children, ...props }, ref) => (
  <div className="d3p-skill-header" {...props} ref={ref}>
    {icon}
    <div className="d3p-skill-description">
      <span className={classNames("d3p-skill-name", {"d3p-skill-none": empty})}>
        {name}
      </span>
      <span className="d3p-skill-separator"/>
      {children}
    </div>
  </div>
));

function SkillTooltip({ info, name }) {
  return info.tips()?.[name]?.tip || null;
}

export function SkillInfo({ type, id, rune }) {
  const state = Store.useState();
  const info = getSkillInfo(state)[type](id, rune);

  const values = info && info.text();
  if (!values) return null;

  return (
    <ul className="d3p-skill-info">
      {Object.entries(values).map(([stat, {text, tip}]) => {
        let line = <span><b>{_L(stat)}:</b> {text}</span>;
        if (tip != null) {
          line = <Tooltip tooltip={<SkillTooltip info={info} name={stat}/>} touchAction="show">{line}</Tooltip>;
        }
        return <li key={stat}>{line}</li>;
      })}
    </ul>
  );
}

export const SkillActive = React.memo(function SkillActive({ info }) {
  const active = Store.useSelector(state => info.selectActive(state), [info]);
  const dispatch = Store.useDispatch();
  const setActive = React.useCallback(e => info.setActive && info.setActive(dispatch, e.target.checked), [info, dispatch]);

  return (
    <div className={classNames("d3p-skill-active", {"d3p-disabled": active == null})}>
      <label>
        <input type="checkbox" checked={active !== false} onChange={setActive} disabled={active == null}/>
        {info.activetip ? _L(info.activetip) : _L`Include in stats`}
      </label>
    </div>
  );
});

export const SkillBoxes = React.memo(function SkillBoxes({ info }) {
  const active = Store.useSelector(state => info.selectActive(state), [info]);
  const values = Store.useSelector(state => info.selectBoxes(state), [info]);
  const dispatch = Store.useDispatch();

  if (active === false) return null;

  return (
    <ul className="d3p-skill-active">
      {info.boxnames.map((name, index) => {
        const onChange = e => info.setBoxValue(dispatch, index, e.target.checked);
        return (
          <li key={index}>
            <label>
              <input type="checkbox" checked={!!values[index]} onChange={onChange}/>
              {typeof name === 'function' ? name() : name}
            </label>
          </li>
        );
      })}
    </ul>
  );
});

function SkillParam({ state, dispatch, info, param, active, skill }) {
  if (active === false && param.buffs !== false) return null;
  if (skill && param.rune && !param.rune.includes(skill[1])) return null;
  if (param.show) {
    const stats = getStats(state);
    if (skill && !param.show.call(info, skill[1], stats)) return null;
    if (!skill && !param.show.call(info, stats)) return null;
  }

  const values = param.selectValue(state);
  if (values.min === values.max) return null;
  const setValue = value => {
    if (value <= values.min) {
      value = (param.dynamic ? "min" : values.min);
    } else if (!values.inf && value >= values.max) {
      value = (param.dynamic ? "max" : values.max);
    }
    param.setValue(dispatch, value);
  };

  const length = (values.max - values.min) / (values.step || 1);
  return (
    <li>
      <span className="d3p-param-name">{values.name}:</span>
      <span className="d3p-param-value">
        <TooltipInput min={values.min}
                      max={values.inf ? null : values.max}
                      step={values.step}
                      value={values.val}
                      onChange={setValue}
        />
      </span>
      <span className="d3p-param-slider">
        <Slider min={values.min}
                max={values.max}
                step={values.step}
                value={values.val}
                infinite={values.inf}
                onChange={setValue}
                style={length < 10 ? {maxWidth: `${length * 40}px`} : null}
        />
      </span>
    </li>
  );
}

export function SkillParams({ info, skill }) {
  const active = Store.useSelector(state => info.selectActive(state), [info]);
  const state = Store.useState();
  const dispatch = Store.useDispatch();

  return (
    <ul className="d3p-skill-params">
      {info.params.map((param, index) => (
        <SkillParam key={index} state={state} dispatch={dispatch} info={info} param={param} active={active} skill={skill}/>
      ))}
    </ul>
  );
}
